import React, { ReactElement, useContext } from 'react';
import styled from 'styled-components';
import { TreeSelect as AntTreeSelect } from 'antd';
import { colorsPalette } from '../../styles/colors-palette';
import { ValidationContext } from './Validation';

type TreeSelectValueType = string | number;

type TreeSelectOption<ParentValueType, ChildValueType> = {
  title: React.ReactNode;
  value: ParentValueType | ChildValueType;
  [x: string]: unknown;
  children?: Omit<TreeSelectOption<ParentValueType, ChildValueType>, 'children'>[];
};

enum TreeSelectCheckedStrategy {
  SHOW_ALL = 'SHOW_ALL',
  SHOW_PARENT = 'SHOW_PARENT',
  SHOW_CHILD = 'SHOW_CHILD',
}

interface ExtraOnChange<ChildValueType> {
  triggerValue: ChildValueType;
  checked: boolean;
  preValue: { value: ChildValueType }[];
}

type TreeSelectSizeType = 'large' | 'middle' | 'small';

interface TreeSelectProps<ParentValueType, ChildValueType> {
  options: TreeSelectOption<ParentValueType, ChildValueType>[];
  values: ChildValueType[];
  onChange: (value: ChildValueType[], extra: ExtraOnChange<ChildValueType>) => void;
  treeCheckable?: boolean;
  treeDefaultExpandAll?: boolean;
  checkedStrategy?: TreeSelectCheckedStrategy;
  maxTagCount?: number | 'responsive';
  maxTagPlaceholder?: () => React.ReactNode;
  placeholder?: PlainText;
  className?: string;
  size?: TreeSelectSizeType;
  dropdownWidth?: number | boolean;
  dropdownRender?: (menu: ReactElement) => ReactElement;
}

function TreeSelect<
  ParentValueType extends TreeSelectValueType,
  ChildValueType extends TreeSelectValueType,
>({
  options,
  values,
  onChange,
  maxTagCount = 'responsive',
  treeCheckable = true,
  checkedStrategy = TreeSelectCheckedStrategy.SHOW_CHILD,
  placeholder,
  treeDefaultExpandAll,
  maxTagPlaceholder,
  className,
  size = 'large',
  dropdownWidth,
  dropdownRender,
}: TreeSelectProps<ParentValueType, ChildValueType>): ReactElement {
  const { validateStatus } = useContext(ValidationContext);

  const antOnChange = (value, title, extra) => {
    onChange && onChange(value, extra);
  };

  return (
    <StyledTreeSelect
      treeData={options}
      value={values}
      maxTagCount={maxTagCount}
      maxTagPlaceholder={maxTagPlaceholder}
      status={validateStatus}
      autoClearSearchValue={false}
      onChange={antOnChange}
      treeCheckable={treeCheckable}
      treeDefaultExpandAll={treeDefaultExpandAll}
      showCheckedStrategy={checkedStrategy}
      placeholder={placeholder}
      size={size}
      className={className}
      treeNodeFilterProp={'title'}
      popupMatchSelectWidth={dropdownWidth}
      dropdownRender={dropdownRender}
    />
  );
}

const StyledTreeSelect = styled(AntTreeSelect)`
  &.ant-select {
    width: 100%;

    &:not(.ant-select-disabled) {
      &:not(.ant-select-customize-input) {
        &:not(.ant-pagination-size-changer) {
          &:hover {
            .ant-select-selector {
              border-color: ${colorsPalette.dustyBlue500};
            }
          }
        }
      }
    }
  }

  &.ant-select-focused {
    &:not(.ant-select-disabled) {
      &:not(.ant-select-customize-input) {
        &:not(.ant-pagination-size-changer) {
          .ant-select-selector {
            border-color: ${colorsPalette.dustyBlue500};
          }
        }
      }
    }
  }
`;

export { TreeSelect, TreeSelectCheckedStrategy, type ExtraOnChange as TreeSelectExtraOnChange };
